// MARCA | 06/05/2026 | New file – Next day delivery countdown custom element for cart
import { Component } from '@theme/component';

/**
 * Displays a live countdown to the next dispatch cutoff.
 * Calculates the next available delivery day based on per-day enabled/cutoff settings,
 * then shows "Order within X to receive by tomorrow" or the equivalent future-day message.
 * Supports blocked dates configured via global theme settings.
 *
 * @typedef {object} Refs
 * @property {HTMLDivElement} container
 * @property {HTMLSpanElement} message
 *
 * @extends {Component<Refs>}
 */
class CartNextDayDeliveryComponent extends Component {
  static DAY_NAMES = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];
  static DAY_DISPLAY = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  /** @type {Record<string, { enabled: boolean, cutoff: string }>} */
  #days = {};

  /** @type {{ nextDay: string, futureDay: string, unavailable: string }} */
  #messages = { nextDay: "", futureDay: "", unavailable: "" };

  /** @type {Set<string>} Blocked dates in YYYY-MM-DD format */
  #blockedDates = new Set();

  /** @type {ReturnType<typeof setInterval> | null} */
  #interval = null;

  connectedCallback() {
    super.connectedCallback();

    for (const day of CartNextDayDeliveryComponent.DAY_NAMES) {
      const enabledAttr = `${day}Enabled`;
      const cutoffAttr = `${day}Cutoff`;
      this.#days[day] = {
        enabled: this.dataset[enabledAttr] === "true",
        cutoff: this.dataset[cutoffAttr] || "14:00",
      };
    }

    this.#messages = {
      nextDay:
        this.dataset.nextDayMessage ||
        "order within {TIME} to receive by {DAY}",
      futureDay:
        this.dataset.futureDayMessage ||
        "order within {TIME} to receive by {DAY}",
      unavailable:
        this.dataset.unavailableMessage || "Next day delivery not available",
    };

    const blockedRaw = this.dataset.blockedDates || "";
    if (blockedRaw.trim()) {
      blockedRaw.split(",").forEach((d) => {
        const trimmed = d.trim();
        if (trimmed) this.#blockedDates.add(trimmed);
      });
    }

    this.#update();
    this.#interval = setInterval(() => this.#update(), 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#interval) {
      clearInterval(this.#interval);
      this.#interval = null;
    }
  }

  /**
   * @param {string} timeStr
   * @returns {{ hours: number, minutes: number }}
   */
  #parseTime(timeStr) {
    const parts = timeStr.split(":").map(Number);
    return { hours: parts[0] || 0, minutes: parts[1] || 0 };
  }

  /**
   * @param {Date} date
   * @returns {string}
   */
  #toDateString(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  /**
   * Find the next day with dispatch enabled and a cutoff still in the future.
   * Looks up to 14 days ahead and respects blocked dates.
   */
  #getNextDeliveryInfo() {
    const now = new Date();

    for (let i = 0; i <= 14; i++) {
      const checkDate = new Date(now);
      checkDate.setDate(now.getDate() + i);

      if (this.#blockedDates.has(this.#toDateString(checkDate))) continue;

      const dayName =
        CartNextDayDeliveryComponent.DAY_NAMES[checkDate.getDay()] || "";
      const daySettings = this.#days[dayName];

      if (!daySettings?.enabled) continue;

      const { hours, minutes } = this.#parseTime(daySettings.cutoff);
      const cutoff = new Date(checkDate);
      cutoff.setHours(hours, minutes, 0, 0);

      if (i === 0 && now >= cutoff) continue;

      const shipDate = new Date(checkDate);
      const deliveryDate = this.#getNextDeliveryDay(shipDate);

      const tomorrow = new Date(now);
      tomorrow.setDate(now.getDate() + 1);
      const isNextDay = deliveryDate.toDateString() === tomorrow.toDateString();

      return {
        cutoff,
        deliveryDayName: isNextDay
          ? "tomorrow"
          : CartNextDayDeliveryComponent.DAY_DISPLAY[deliveryDate.getDay()],
        isNextDay,
        isAvailable: true,
      };
    }

    return {
      cutoff: null,
      deliveryDayName: "",
      isNextDay: false,
      isAvailable: false,
    };
  }

  /**
   * @param {Date} date
   * @returns {Date}
   */
  #getNextDeliveryDay(date) {
    const next = new Date(date);
    next.setDate(date.getDate() + 1);
    return next;
  }

  /**
   * MARCA | 06/05/2026 | Changed to HH:MM:SS format for countdown display
   * MARCA | 15/05/2026 | Added h m s suffixes to countdown format
   * @param {number} ms
   * @returns {string}
   */
  #formatCountdown(ms) {
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;

    const pad = (/** @type {number} */ n) => String(n).padStart(2, "0");
    return `${pad(h)}h:${pad(m)}m:${pad(s)}s`;
  }

  #update() {
    const { container, message } = this.refs;
    if (!container || !message) return;

    const info = this.#getNextDeliveryInfo();

    if (!info.isAvailable || !info.cutoff) {
      container.hidden = true;
      return;
    }

    container.hidden = false;

    const remaining = info.cutoff.getTime() - Date.now();

    if (remaining <= 0) {
      setTimeout(() => this.#update(), 100);
      return;
    }

    const countdown = this.#formatCountdown(remaining);
    const timeHtml = `<span class="cart-next-day-delivery__time">${countdown}</span>`;
    const dayHtml = `<span class="cart-next-day-delivery__day">${info.deliveryDayName}</span>`;

    const template = info.isNextDay
      ? this.#messages.nextDay
      : this.#messages.futureDay;
    message.innerHTML = template
      .replace(/\{TIME\}/gi, timeHtml)
      .replace(/\{DAY\}/gi, dayHtml);

    container.classList.add("cart-next-day-delivery--active");
    container.classList.remove("cart-next-day-delivery--unavailable");
  }
}

if (!customElements.get('cart-next-day-delivery')) {
  customElements.define('cart-next-day-delivery', CartNextDayDeliveryComponent);
}
